/**
 * pages/types.ts —— 页面模块契约
 * 出处：06_二期技术方案.md §三之五 风险①（渲染层异步化）
 *
 * ## 为什么要有这个契约
 * 一期的页面是 `main.innerHTML = overviewHtml()` 一句话，同步读常量。
 * 二期每个页面都要 await fetch，如果让各页自己管取数、加载态、错误处理、竞态，
 * 12 个页面就会有 12 套写法，且必然有几个漏掉其中一件。
 * 把这四件收进 router/render.ts，页面只需要填四个格子：
 *
 *   load()     唯一取数处。只调 api/，不碰 DOM。
 *   html()     纯函数：数据进、字符串出。**不许 fetch、不许算口径**（红线 4）。
 *   mounted()  DOM 插入之后才能做的事：滚动位置、聚焦、量宽度。
 *   onEvent()  本页的事件分支，由 shared/events.ts 转发过来。
 *
 * ## 三条纪律
 * 1. **html() 保持纯函数。** 它是唯一能不起浏览器就测的部分，也是从一期搬渲染代码时
 *    改动最小的形态——一期那些 xxxHtml() 本来就是纯的。
 * 2. **页面里不写 try/catch。** load() 抛出去，render 统一渲染错误态 + 重试。
 *    自己 catch 掉会让失败变成一个安静的空页面。
 * 3. **页面里不写 loading。** 骨架与进度条由 render 按 skeleton 字段统一给。
 */
import type { Route } from '../router/parse.js';
import type { SkeletonKind } from '../shared/feedback.js';

export interface LoadCtx {
  route: Route;
  /** 切页时会 abort，传给 api 调用即可自动丢弃过期请求 */
  signal: AbortSignal;
}

export interface PageModule<D = unknown> {
  /** 加载中显示哪种骨架。挑最接近本页版式的一个，不要新造 */
  skeleton: SkeletonKind;
  /** 唯一取数处。⚠ 只调 api/，不碰 DOM，不吞异常 */
  load(ctx: LoadCtx): Promise<D>;
  /** 纯函数：数据进、HTML 字符串出 */
  html(data: D, route: Route): string;
  /** DOM 插入之后：恢复滚动位置、聚焦、量宽度（如快报的 blFit） */
  mounted?(data: D, route: Route): void;
  /** 本页事件分支。由全局委托转发，⚠ 不要在页面里自己 addEventListener 到 document */
  onEvent?(e: Event, data: D, route: Route): void;
}

/** 定义页面模块的小助手：只为让 D 被推断出来，不做任何事 */
export const definePage = <D>(m: PageModule<D>): PageModule<D> => m;
